import { CommonRequest, CommonResponse, Method, ServiceName } from 'models/common/RestApi';
import { callApi } from 'utils/ApiUtil';
import { SetupStep, SetupStepCondition } from 'models/setup/SetupStep';

/**
 * 셋업단계 목록 조회
 * @param condition
 */
export const findSetupStep = async (condition?: SetupStepCondition) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/step`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ ...condition }),
  };

  const response: CommonResponse<[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as SetupStep[];
};

/**
 * 프로젝트 셋업단계 목록 저장
 * @param items
 */
export const saveSetupStep = async (items?: SetupStep[]) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/step`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: items,
  };

  const response: CommonResponse<SetupStep[]> = await callApi(request);
  return response;
};

/**
 * 프로젝트 공통단계 목록 조회
 * @param condition
 */
export const findProjectCommonStep = async (condition?: SetupStep) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/project-step/common`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ ...condition }),
  };

  const response: CommonResponse<[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as SetupStep[];
};

/**
 * 프로젝트 공통단계 목록 저장
 * @param items
 */
export const saveProjectCommonStep = async (items?: SetupStep[]) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/project-step/common`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: items,
  };

  const response: CommonResponse<SetupStep[]> = await callApi(request);
  return response;
};

/**
 * 프로젝트 단계유형 목록 조회
 * @param elmPrjId
 */
export const findProjectStepType = async (elmPrjId?: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/project-step/type`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ elmPrjId: elmPrjId || '' }),
  };

  const response: CommonResponse<[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as SetupStep[];
};

/**
 * 프로젝트 단계유형 목록 조회 (셋업단계 설정 검증이 완료된 단계유형만)
 * (설비 등록 시 사용되는 셋업단계 검증 통과한 단계유형)
 * @param elmPrjId
 */
export const findValidProjectStepType = async (elmPrjId?: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/project-step/type/valid`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ elmPrjId: elmPrjId || '' }),
  };

  const response: CommonResponse<[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as SetupStep[];
};

/**
 * 프로젝트 단계유형 목록 저장
 * @param items
 */
export const saveProjectStepType = async (items?: SetupStep[]) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/project-step/type`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: items,
  };

  const response: CommonResponse<SetupStep[]> = await callApi(request);
  return response;
};

/**
 * 프로젝트 설비군별 단계 목록 조회
 * @param condition {elmPrjId: 프로젝트ID, spTpCd: 단계유형코드, autoCalYn: 자동계산여부}
 */
export const findProjectStepBySpTpCd = async (condition?: SetupStep) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/project-step/type/step`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ ...condition }),
  };

  const response: CommonResponse<[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as SetupStep[];
};

/**
 * 프로젝트 전체 단계 목록 조회 (검색조건용)
 * 전체 설비군별 단계 목록에서 중복제거한 전체 목록 조회
 * @param elmPrjId 프로젝트ID
 * @param eqclId   설비군ID
 */
export const findProjectAllStep = async (elmPrjId: string, eqclId?: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/project-step/all-step`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ elmPrjId: elmPrjId, eqclId: eqclId || '' }),
  };

  const response: CommonResponse<[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as SetupStep[];
};

/**
 * 프로젝트 셋업단계 설정 목록 조회
 * @param condition
 */
export const findProjectStep = async (condition?: SetupStepCondition) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/project-step`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ ...condition }),
  };

  const response: CommonResponse<[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as SetupStep[];
};

/**
 * 프로젝트 단계유형별 설정가능 상태정보 조회
 * @param elmPrjId
 */
export const findProjectStepSetupStatus = async (elmPrjId) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/project-step/setup-status`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ elmPrjId: elmPrjId }),
  };

  const response: CommonResponse = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as SetupStep[];
};

/**
 * 프로젝트 셋업단계 설정 > 셋업 단계 목록 조회
 * @param condition
 */
export const findProjectStepSetupStep = async (condition?: SetupStep) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/project-step/setup-step`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ ...condition }),
  };

  const response: CommonResponse = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : undefined) as any;
};
/**
 * 프로젝트 셋업단계 설정 > 셋업단계 저장
 * @param items
 */
export const saveProjectStepSetupStep = async (items?: SetupStep[]) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/project-step/setup-step`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: items,
  };

  const response: CommonResponse<SetupStep[]> = await callApi(request);
  return response;
};
/**
 * 프로젝트 셋업단계 설정 > 설정완료 전 정상데이터 검증
 * 단계유형, 공통단계, 단계유형별 셋업단계의 검증
 * @param elmPrjId 프로젝트ID
 */
export const validateProjectStepSetup = async (elmPrjId: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/project-step/setup-validate`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ elmPrjId: elmPrjId }),
  };

  const response: CommonResponse = await callApi(request);
  return response;
};

/**
 * 프로젝트 셋업단계 설정 > 설정완료 처리 (단계설정완료여부 Y)
 * @param elmPrjId 프로젝트ID
 */
export const completeProjectStepSetup = async (elmPrjId: string) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/project-step/${elmPrjId}/setup-complete`,
    serviceName: ServiceName.ELM_BE,
  };

  const response: CommonResponse = await callApi(request);
  return response;
};

/**
 * 프로젝트 셋업단계 설정 > 단계유형, 공통단계, 셋업단계 복사
 * @param elmPrjId
 * @param items
 */
export const copyProjectStepSetupStep = async (elmPrjId: string, items?: SetupStep[]) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/project-step/${elmPrjId}/copy`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: items,
  };

  const response: CommonResponse<SetupStep[]> = await callApi(request);
  return response;
};
